import Button from 'components/Button'
import { useCollectiveContext } from 'context/CollectiveContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowAltCircleDown,
  faChevronDown,
  faFileExport
} from '@fortawesome/pro-solid-svg-icons'
import DropdownMenu from 'components/common/Dropdown/DropdownMenu'
import {
  FileExportStatus,
  FileExportType,
  useCreateMemberFileExportMutation,
  useCollectiveMemberFileExportQueryLazyQuery
} from 'graphql/generated'
import useToasts from 'hooks/useToasts'
import TrackableClick from 'components/common/TrackableClick'

export default function CollectiveMembersExport() {
  const { collective } = useCollectiveContext()
  const { addToast } = useToasts()

  const [nameFilter, setNameFilter] = useState(false)
  const [walletAddressFilter, setWalletAddressFilter] = useState(false)
  const [tokenBalanceFilter, setTokenBalanceFilter] = useState(false)
  const [eventsAttendedFilter, setEventsAttendedFilter] = useState(false)

  const [exporting, setExporting] = useState(false)
  const [polling, setPolling] = useState(false)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timer | null>(null)

  const buttonRef = useRef<HTMLButtonElement>(null)

  const onError = () => {
    resetState()
    addToast('An error has occurred please try again later.', {
      appearance: 'warning'
    })
  }

  const [
    createMembersFileExport,
    { data: createdMembersFileExportData, reset: resetCreateMembersFileExport }
  ] = useCreateMemberFileExportMutation({
    variables: {
      collectiveId: collective.id,
      where: {
        type: FileExportType.MEMBER,
        fields: {
          memberFileExportFields: {
            name: nameFilter,
            address: walletAddressFilter,
            totalTokens: tokenBalanceFilter,
            events: eventsAttendedFilter
          }
        }
      }
    },
    onError
  })

  const [
    loadMembersFileExport,
    {
      data: loadedMembersFileExportData,
      startPolling,
      stopPolling,
      loading: loadingMembersFileExport
    }
  ] = useCollectiveMemberFileExportQueryLazyQuery({
    onError
  })

  const createdMembersFileExport =
    createdMembersFileExportData?.createMemberFileExport
  const fileExport = loadedMembersFileExportData?.collective?.memberFileExport

  const handleExport = () => {
    if (
      [
        nameFilter,
        walletAddressFilter,
        tokenBalanceFilter,
        eventsAttendedFilter
      ].every(filter => !filter)
    ) {
      addToast('At least one field must be selected.', {
        appearance: 'warning'
      })
      return
    }

    createMembersFileExport()

    // hide the dropdown to prevent double exports
    buttonRef.current?.click()
    setExporting(true)
  }

  const handleButtonClick = e => {
    // prevent the dropdown from opening when exporting is taking place
    if (exporting) {
      e.stopPropagation()
    }
  }

  const resetState = useCallback(() => {
    setPolling(false)
    stopPolling()

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    setTimeoutId(null)
    setNameFilter(false)
    setWalletAddressFilter(false)
    setTokenBalanceFilter(false)
    setEventsAttendedFilter(false)
    setExporting(false)
    resetCreateMembersFileExport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling, timeoutId])

  const downloadFile = useCallback(() => {
    if (!fileExport?.file) {
      return
    }

    const element = document.createElement('a')
    element.href = fileExport.file
    element.download = fileExport.file.split('/')[-1]
    document.body.appendChild(element)
    element.click()
  }, [fileExport?.file])

  useEffect(() => {
    if (createdMembersFileExport) {
      const fileExportStatus = fileExport?.status
      const isCompleted =
        fileExportStatus === FileExportStatus.ERROR ||
        fileExportStatus === FileExportStatus.FINISHED
      const differentExport = createdMembersFileExport.id !== fileExport?.id

      if (
        (!fileExportStatus || (differentExport && fileExportStatus)) &&
        !polling
      ) {
        loadMembersFileExport({
          variables: {
            collectiveId: collective.id,
            fileExportId: createdMembersFileExport.id
          }
        })

        startPolling(3000)
        setPolling(true)

        // stop polling after 20 seconds if the fileExport hasn't finished
        const id = setTimeout(() => {
          resetState()
          addToast('Your export has timed out. Please try again later.', {
            appearance: 'warning'
          })
        }, 20000)

        setTimeoutId(id)
      } else if (fileExportStatus && isCompleted && polling) {
        downloadFile()
        resetState()
      }
    }
  }, [
    createdMembersFileExport,
    fileExport?.status,
    startPolling,
    stopPolling,
    downloadFile,
    resetState,
    loadMembersFileExport,
    collective.id,
    polling,
    fileExport?.id,
    loadingMembersFileExport,
    addToast
  ])

  return (
    <TrackableClick
      event="Members: Export dropdown Clicked"
      props={{
        collectiveId: collective.id,
        collectiveName: collective.name
      }}
      trackAll
    >
      <DropdownMenu
        button={
          <Button
            color={'white'}
            size="sm"
            icon={faFileExport}
            label="Export Members"
            showFocusRing={false}
            loading={exporting}
            ref={buttonRef}
            onClick={e => handleButtonClick(e)}
          >
            {!exporting && (
              <FontAwesomeIcon
                icon={faChevronDown}
                className="ml-2 text-gray-400"
              />
            )}
          </Button>
        }
        items={[
          { type: 'header', label: 'Select fields' },
          {
            type: 'checkbox',
            label: 'Name',
            onChange: () => setNameFilter(prev => !prev),
            checked: nameFilter
          },
          {
            type: 'checkbox',
            label: 'Wallet Address',
            onChange: () => setWalletAddressFilter(prev => !prev),
            checked: walletAddressFilter
          },
          {
            type: 'checkbox',
            label: 'Token Balance',
            onChange: () => setTokenBalanceFilter(prev => !prev),
            checked: tokenBalanceFilter
          },
          {
            type: 'checkbox',
            label: 'Events Attended',
            onChange: () => setEventsAttendedFilter(prev => !prev),
            checked: eventsAttendedFilter
          },
          { type: 'separator' },
          {
            type: 'button',
            label: 'Export',
            icon: faArrowAltCircleDown,
            eventName: 'Members: Export button clicked',
            eventProps: {
              collectiveId: collective.id,
              collectiveName: collective.name,
              nameFilter,
              walletAddressFilter,
              tokenBalanceFilter,
              eventsAttendedFilter
            },
            onClick: () => handleExport()
          }
        ]}
      />
    </TrackableClick>
  )
}
